import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { Base64 } from 'js-base64';

import { ACTIVE_DIRECTORY, AUTH_CONSTANTS } from '@app/shared/constants';
import { ParsedAccessToken, ParsedRefreshToken, UserInfo } from '@app/shared/models';
import { TokensApiModel } from '@app/api/api-models';

@Injectable()
export class TokenStorageService implements OnDestroy {
  public tokensChange = new EventEmitter<TokensApiModel>();
  public userInfoChanged = new EventEmitter<UserInfo>();
  public get userInfo(): UserInfo {
    return this._userInfo;
  }

  public get signedUpDate(): string {
    return this._signedUp;
  }

  public set signedUpDate(date: string) {
    this._signedUp = date;
  }

  private _timeDiff: number;
  private _userInfo: UserInfo;
  private _signedUp: string;
  private subs: Subscription[] = [];

  constructor() {
    const lastDiff = localStorage.getItem(AUTH_CONSTANTS.TIME_DIFF_KEY);

    this._timeDiff = lastDiff ? +lastDiff : 0;

    const existingToken = this.getAccToken();
    const existingRefreshToken = this.getRefToken();

    if (existingToken && existingRefreshToken && !this.isTokenExpired(existingRefreshToken)) {
      this.setUserInfo(existingToken);
    }

    this.subs.push(
      this.tokensChange.subscribe(this.setTokens.bind(this)),
      this.tokensChange.subscribe(this.updateUserInfo.bind(this))
    );
  }

  public ngOnDestroy(): void {
    this.unsubscribe();
  }

  private updateUserInfo(tokens: TokensApiModel): void {
    this.setUserInfo(tokens.AccessToken, true);
  }

  private setUserInfo(accessToken: string, updateTimeDiff = false): void {
    const parsedToken = this.getParsedToken(accessToken);

    if (updateTimeDiff) {
      this._timeDiff = this.getCurrentUnixTime() - parsedToken.nbf;
      localStorage.setItem(AUTH_CONSTANTS.TIME_DIFF_KEY, this._timeDiff.toString());
    }

    this._userInfo = new UserInfo(parsedToken as ParsedAccessToken);
    this.userInfoChanged.emit(this.userInfo);
  }

  public clearStorageData(): void {
    localStorage.removeItem(AUTH_CONSTANTS.TOKEN_LS_KEY);
    localStorage.removeItem(AUTH_CONSTANTS.TOKEN_SS_KEY);
    // SDK handles its own storage - no need to clear EID_CONFIGID
    localStorage.removeItem(ACTIVE_DIRECTORY.activeDirectorySettings);
    this._userInfo = UserInfo.empty;
  }

  public getClientId(): string {
    return localStorage.getItem(AUTH_CONSTANTS.CLIENT_ID);
  }

  public getRefToken(): string {
    return localStorage.getItem(AUTH_CONSTANTS.TOKEN_LS_KEY);
  }

  public getPayloadJson(token: string): ParsedAccessToken | ParsedRefreshToken {
    return JSON.parse(Base64.decode(token.split('.')[1]));
  }

  public getAccToken(): string {
    return localStorage.getItem(AUTH_CONSTANTS.TOKEN_SS_KEY);
  }

  public setTokens(tokens: TokensApiModel): void {
    localStorage.setItem(AUTH_CONSTANTS.TOKEN_LS_KEY, tokens.RefreshToken);
    localStorage.setItem(AUTH_CONSTANTS.CLIENT_ID, tokens.ClientId);
    localStorage.setItem(AUTH_CONSTANTS.TOKEN_SS_KEY, tokens.AccessToken);
    this._signedUp = undefined;
  }

  public isTokenExpired(token: string): boolean {
    const exp = this.getParsedToken(token).exp;

    if (exp === undefined) {
      return true;
    }

    const current = this.getCurrentUnixTime();

    const expired = current - this._timeDiff + 70 > exp;

    return expired;
  }

  public getParsedToken(token: string): ParsedAccessToken | ParsedRefreshToken {
    return this.getPayloadJson(token);
  }

  public isSameUser(userId: string): boolean {
    if (this._userInfo) {
      return this._userInfo.UserId === userId;
    }

    return false;
  }

  private unsubscribe(): void {
    this.subs.forEach((sub: Subscription) => {
      sub.unsubscribe();
    });
  }

  private getCurrentUnixTime(): number {
    return Math.ceil(new Date().getTime() / 1000);
  }
}
